import React, { useContext, useState } from "react";
import {
  GridContainer,
  AcquirerListRootArea,
  GridItem,
} from "./Acquirer.styles";
import { Button, Typography, Tabs, Tab, Box } from "@mui/material";
import DataTable from "../../dataTable/DataTable";
import ListSearchField from "../../listSearchField/ListSearchField";
import { useEffectOnce } from "../../../utils/effects";
import AuthContext from "../../../context/AuthContext";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useDispatch, useSelector } from "react-redux";
import { getAcquirerList } from "./Acquirer.thunk";
import { SetAcquirerSelected } from "./Acquirer.slice";
import { ResetStore } from "../../forms/acquirer/AcquirerForm.slice";
import LoadAcquirerEnrollment from "../LoadAcquirerEnrollment/LoadAcquirerEnrollment";
import AcquirerShow from "./Acquirer.show";

/**
 * AcquirerList Component
 *
 * This component displays a table of acquirers with detailed information.
 * It fetches acquirer data from the backend API when the component mounts,
 * using the user's token from AuthContext.
 *
 * Features:
 * - Fetches paginated acquirer data for the authenticated user.
 * - Displays data in a table with columns: Acquirer ID, Acquirer Name, Address,
 *   City, State, Country Code, Currency Code and Actions.
 * - Shows the total number of records and supports pagination (page, pageSize, totalRecords).
 * - Allows editing and adding new acquirers.
 * - Includes two tabs: "Acquirer" (main table) and "Draft" (enrollment drafts).
 * - The Draft tab shows incomplete acquirer enrollments with edit and delete actions.
 *
 * Dependencies:
 * - Uses Material-UI (MUI) for layout, typography, and tabs.
 * - Uses a custom DataTable component to display tabular data.
 * - Relies on AuthContext for authentication information.
 * - Uses a custom useEffectOnce hook to fetch data only once when mounting.
 * - Integrates with Redux for state management and async actions.
 * - Includes LoadAcquirerEnrollment component for draft enrollments.
 *
 * @component
 * @param {Object} props - Component properties
 * @param {Function} props.setSubmenuAdministrativeOpen - Function to set the submenu
 * @param {Function} props.setSelectedOption - Function to set the selected option
 * @param {Function} props.setNameSection - Function to set the section name
 */
const AcquirerList = (props) => {
  const {
    setSubmenuAdministrativeOpen,
    setSubmenuAdministrativeOption,
    setSelectedOption,
    setNameSection,
  } = props;
  const dispatch = useDispatch();
  const { acquirerData, page, pageSize, totalRecords, status } = useSelector(
    (state) => state.acquirer
  );
  const { token } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState(0);
  const [acquirerId, setAcquirerId] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const buildListPayload = (overrides = {}) => {
    const search = "search" in overrides ? overrides.search : appliedSearch;
    return {
      token,
      page: overrides.page ?? page,
      page_size: overrides.page_size ?? pageSize,
      ...(search && { search }),
    };
  };

  const loadList = (overrides = {}) => {
    dispatch(getAcquirerList(buildListPayload(overrides)));
  };

  const handleSearch = () => {
    const nextSearch = searchValue.trim();
    setAppliedSearch(nextSearch);
    loadList({ page: 1, search: nextSearch });
  };

  const handleClearSearch = () => {
    setSearchValue("");
    setAppliedSearch("");
    loadList({ page: 1, search: "" });
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  useEffectOnce(() => {
    loadList();
    dispatch(ResetStore());
  });

  const handleEdit = async ({ acquirer_id }) => {
    await dispatch(ResetStore());
    dispatch(SetAcquirerSelected({ acquirerSelected: acquirer_id }));
    setSubmenuAdministrativeOpen(true);
    setSubmenuAdministrativeOption("acquirer_list");
    setSelectedOption("acquirer_form");
    setNameSection("Administrative - Acquirer Form");
  };

  const handleAdd = () => {
    dispatch(SetAcquirerSelected({ acquirerSelected: null }));
    setSubmenuAdministrativeOpen(true);
    setSubmenuAdministrativeOption("acquirer_list");
    setSelectedOption("acquirer_form");
    setNameSection("Administrative - Acquirer Form");
  };

  const handleShow = async ({ acquirer_id }) => {
    await dispatch(ResetStore());
    setAcquirerId(acquirer_id);
  };

  const handlePageChange = (event, newPage) => {
    loadList({ page: newPage + 1 });
  };

  const handleRowsPerPageChange = (event) => {
    const newPageSize = +event.target.value;
    loadList({ page: 1, page_size: newPageSize });
  };

  const columns = [
    { id: "acquirer_id", label: "Acquirer ID" },
    { id: "acquirer_name", label: "Acquirer Name" },
    { id: "address", label: "Address" },
    {
      id: "city",
      label: "City",
    },
    { id: "state", label: "State" },
    { id: "country_code_alpha2", label: "Country Code" },
    { id: "currency_alpha3_code", label: "Currency Code" },
    {
      id: "action",
      label: "Actions",
      actions: [
        {
          icon: <VisibilityIcon />,
          label: "Show",
          onClick: (row) => handleShow(row),
        },
        {
          icon: <EditIcon />,
          label: "Set to Complete",
          onClick: (row) => handleEdit(row),
        },
      ],
    },
  ];

  return (
    <AcquirerListRootArea>
      <GridContainer>
        <GridContainer justifyContent="space-between" alignItems="center">
          <GridItem xs={10}>
            <Typography variant="h6" sx={{ mb: 2, mt: 2 }}>
              Acquirer
            </Typography>
          </GridItem>
          <GridItem xs={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAdd}
              size="small"
            >
              Add
            </Button>
          </GridItem>
        </GridContainer>

        {/* Tabs */}
        <GridItem xs={12}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="acquirer tabs"
            >
              <Tab label="Active" />
              <Tab label="In Progress" />
            </Tabs>
          </Box>
        </GridItem>

        {/* Tab Content */}
        <GridItem xs={12}>
          {activeTab === 0 && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ mb: 2, maxWidth: { xs: "100%", sm: 360 } }}>
                <ListSearchField
                  fullWidth
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onSearch={handleSearch}
                  placeholder="Acquirer ID or name"
                />
              </Box>
              <DataTable
                loading={status === "loading"}
                columns={columns}
                rows={acquirerData}
                page={page - 1}
                rowsPerPage={pageSize}
                count={totalRecords}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </Box>
          )}

          {activeTab === 1 && (
            <Box sx={{ mt: 2 }}>
              <LoadAcquirerEnrollment
                setSubmenuAdministrativeOption={setSubmenuAdministrativeOption}
                setSubmenuAdministrativeOpen={setSubmenuAdministrativeOpen}
                setSelectedOption={setSelectedOption}
                setNameSection={setNameSection}
              />
            </Box>
          )}
        </GridItem>
      </GridContainer>
      <AcquirerShow acquirerId={acquirerId} setAcquirerId={setAcquirerId} />
    </AcquirerListRootArea>
  );
};

export default AcquirerList;
